import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api, type FantasyLeagueSummary } from '../../api'
import { actionButton } from '../ui/variants'
import { useAuth } from '../../auth'

/**
 * Leagues: yours, the public ones, and the forms for making or joining one.
 *
 * Deliberately off the main path. Everybody lands on the board; this is for the minority who run
 * their own league or were handed an invite, and it is reached from the switcher in the board's
 * header rather than by making everyone walk past it first.
 */
export function LeagueIndex({ go }: { go: (...segments: (string | number)[]) => void }) {
  const { me, signOut } = useAuth()
  const queryClient = useQueryClient()
  const [inviteToken, setInviteToken] = useState('')
  const [creating, setCreating] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const mine = useQuery({ queryKey: ['fantasy', 'mine'], queryFn: api.myFantasyLeagues })
  const directory = useQuery({ queryKey: ['fantasy', 'directory'], queryFn: api.fantasyDirectory })

  const join = useMutation({
    mutationFn: (token: string) => api.joinFantasyLeague(token),
    onSuccess: (league) => {
      setInviteToken('')
      setError(null)
      void queryClient.invalidateQueries({ queryKey: ['fantasy'] })
      go(league.id)
    },
    onError: (failure: Error) => setError(failure.message),
  })

  const mineIds = new Set((mine.data ?? []).map((league) => league.id))
  const joinable = (directory.data ?? []).filter((league) => !mineIds.has(league.id))

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <button
            type="button"
            onClick={() => go()}
            className="text-xs text-ink-faint hover:text-ink"
          >
            ← Back to the game
          </button>
          <h2 className="mt-2 text-2xl font-semibold">Leagues</h2>
        </div>
        <div className="text-right text-xs text-ink-faint">
          <div>Signed in as {me?.handle}</div>
          <button type="button" onClick={signOut} className="mt-1 hover:text-ink">
            Sign out
          </button>
        </div>
      </div>

      <section className="space-y-2">
        <h3 className="text-sm font-medium text-ink-soft">Yours</h3>
        {mine.isPending ? (
          <p className="text-sm text-ink-faint">Loading…</p>
        ) : (
          <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
            {(mine.data ?? []).map((league) => (
              <LeagueCard key={league.id} league={league} onOpen={() => go(league.id)} />
            ))}
          </div>
        )}
      </section>

      {joinable.length > 0 ? (
        <section className="space-y-2">
          <h3 className="text-sm font-medium text-ink-soft">Open to anyone</h3>
          <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
            {joinable.map((league) => (
              <LeagueCard key={league.id} league={league} onOpen={() => go(league.id)} />
            ))}
          </div>
        </section>
      ) : null}

      <section className="grid gap-4 lg:grid-cols-2">
        <div className="min-w-0 rounded-lg border border-line bg-surface p-4">
          <h3 className="text-sm font-medium">Got an invite?</h3>
          <p className="mt-1 text-xs text-ink-faint">
            A private league is invisible until somebody hands you a link. Paste the token here.
          </p>
          <div className="mt-3 flex flex-wrap gap-2">
            <input
              value={inviteToken}
              onChange={(event) => setInviteToken(event.target.value)}
              placeholder="invite token"
              className="min-h-11 min-w-0 flex-1 rounded border border-line bg-surface-raised px-2 text-sm text-ink"
            />
            <button
              type="button"
              disabled={inviteToken.trim().length === 0 || join.isPending}
              onClick={() => join.mutate(inviteToken.trim())}
              className={actionButton()}
            >
              Join
            </button>
          </div>
          {error !== null ? <p className="mt-2 text-xs text-danger">{error}</p> : null}
        </div>

        <div className="min-w-0 rounded-lg border border-line bg-surface p-4">
          <h3 className="text-sm font-medium">Start your own</h3>
          <p className="mt-1 text-xs text-ink-faint">
            You run it: you pick which tournaments each season follows, when it opens, and whether
            the bots play.
          </p>
          {creating ? (
            <CreateLeagueForm
              onDone={(id) => {
                setCreating(false)
                void queryClient.invalidateQueries({ queryKey: ['fantasy'] })
                go(id)
              }}
              onCancel={() => setCreating(false)}
            />
          ) : (
            <button
              type="button"
              onClick={() => setCreating(true)}
              className={actionButton({ className: 'mt-3' })}
            >
              Create a league
            </button>
          )}
        </div>
      </section>
    </div>
  )
}

function LeagueCard({ league, onOpen }: { league: FantasyLeagueSummary; onOpen: () => void }) {
  return (
    <button
      type="button"
      onClick={onOpen}
      // min-w-0 on the card, not only on the truncating child: a grid item defaults to a minimum of
      // its content's width, which is what once pushed the whole page sideways on a phone.
      className="min-w-0 rounded-lg border border-line bg-surface p-3 text-left transition-colors hover:border-accent-edge"
    >
      <div className="flex items-start justify-between gap-2">
        <span className="min-w-0 truncate text-sm">{league.name}</span>
        {league.isHouse ? (
          <span className="shrink-0 rounded bg-accent-bg px-1.5 py-0.5 text-[10px] uppercase tracking-wide text-accent">
            House
          </span>
        ) : null}
      </div>
      <div className="tabular mt-1 text-xs text-ink-faint">
        {league.members} {league.members === 1 ? 'member' : 'members'}
        {league.role === 'manager' ? ' · you manage it' : ''}
      </div>
    </button>
  )
}

function CreateLeagueForm({ onDone, onCancel }: {
  onDone: (id: number) => void
  onCancel: () => void
}) {
  const [name, setName] = useState('')
  const [isPublic, setIsPublic] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const create = useMutation({
    mutationFn: () => api.createFantasyLeague(name.trim(), null, isPublic),
    onSuccess: (league) => onDone(league.id),
    onError: (failure: Error) => setError(failure.message),
  })

  return (
    <div className="mt-3 space-y-3">
      <input
        value={name}
        onChange={(event) => setName(event.target.value)}
        placeholder="League name"
        className="min-h-11 w-full rounded border border-line bg-surface-raised px-2 text-sm text-ink"
      />

      <label className="flex items-start gap-2 text-xs text-ink-soft">
        <input
          type="checkbox"
          checked={isPublic}
          onChange={(event) => setIsPublic(event.target.checked)}
          className="mt-0.5"
        />
        <span>
          List it publicly. Leave this off and the league is invisible to everyone without an
          invite — not merely closed to them, but not there at all.
        </span>
      </label>

      {error !== null ? <p className="text-xs text-danger">{error}</p> : null}

      <div className="flex flex-wrap gap-2">
        <button
          type="button"
          disabled={name.trim().length < 3 || create.isPending}
          onClick={() => create.mutate()}
          className={actionButton()}
        >
          Create
        </button>
        <button
          type="button"
          onClick={onCancel}
          className="min-h-11 rounded px-3 text-sm text-ink-faint hover:text-ink"
        >
          Cancel
        </button>
      </div>
    </div>
  )
}
